import React, {
  useCallback, useMemo, useState,
} from 'react';
import {
  Button,
  IconCalendar,
  IconDownload,
  IconFilledInfo,
  Skeleton,
  Tag,
} from 'gatorcomponents';
import useLocale from '@/hooks/useLocale';
import {
  Table,
  TablePagination,
  useTableContext,
} from '@/components/Table/Table';
import { useIsMdUp } from '../../../../../components/Table/table.utils';
import { GatorProtectPanel } from '../../GatorProtectHandler.styles';
import * as S from './ThreatsTableSection.styles';
import type {
  GatorProtectReduxState,
  GatorProtectThreatFile,
  GatorProtectThreatsTableLocale,
  requestPlanThreatsPorps,
  StatusOptionProps,
  TagsLocaleTypes,
  ThreatSituationCellProps,
  ThreatsTableColumns,
  ThreatsTableLayoutProps,
} from './model';
import {
  ellipsisThreatPathForMobile,
  formatThreatDateTime,
  resolveThreatSituationTag,
} from './threatsTableSection.utils';
import { useSelector } from 'react-redux';

const ThreatSituationCell = ({ row, tagsLocale }: ThreatSituationCellProps) => {
  const { label, variant } = resolveThreatSituationTag({
    status: row?.status?.toLocaleLowerCase() as StatusOptionProps,
    tagsLocale,
  });

  return (
    <Tag
      label={label}
      variant={variant}
      size="sm"
    />
  );
};

const ThreatsTableLayout = ({
  columns, loading, tagsLocale,
}: ThreatsTableLayoutProps) => {
  const { sortedData, isDesktop } = useTableContext<GatorProtectThreatFile>();

  if (loading) {
    return (
      <>
        <Table.Scroll>
          <Table.Table>
            <thead>
              <tr>
                <Table.Th>{columns.file}</Table.Th>
                <Table.Th>{columns.threat}</Table.Th>
                <Table.Th>{columns.situation}</Table.Th>
                <Table.Th>{columns.detectedAt}</Table.Th>
              </tr>
            </thead>
          </Table.Table>
        </Table.Scroll>
        <S.SkeletonWrapper>
          {Array.from({ length: 5 }).map((_: unknown, index) => (
          // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>
              <Skeleton width="100%" height="40px" />
            </React.Fragment>
          ))}
        </S.SkeletonWrapper>
      </>
    );
  }

  if (!isDesktop) {
    return (
      <S.ThreatsMobileList>
        {sortedData.map(row => (
          <S.ThreatsMobileCard key={row.path}>
            <S.ThreatsMobileField data-column-id="path">
              <S.ThreatsMobileLabel>{`${columns.file}:`}</S.ThreatsMobileLabel>
              <S.ThreatsMobileValue>
                {ellipsisThreatPathForMobile(row.path)}
              </S.ThreatsMobileValue>
            </S.ThreatsMobileField>
            <S.ThreatsMobileField data-column-id="name">
              <S.ThreatsMobileLabel>{`${columns.threat}:`}</S.ThreatsMobileLabel>
              <S.ThreatsMobileValue>{row.name}</S.ThreatsMobileValue>
            </S.ThreatsMobileField>
            <S.ThreatsMobileField data-column-id="status">
              <S.ThreatsMobileLabel>{`${columns.situation}:`}</S.ThreatsMobileLabel>
              <S.ThreatsMobileValue>
                <ThreatSituationCell row={row} tagsLocale={tagsLocale} />
              </S.ThreatsMobileValue>
            </S.ThreatsMobileField>
            <S.ThreatsMobileField data-column-id="detectedAt">
              <S.ThreatsMobileLabel>{`${columns.detectedAt}:`}</S.ThreatsMobileLabel>
              <S.ThreatsMobileValue>
                {formatThreatDateTime(row.detectedAt)}
              </S.ThreatsMobileValue>
            </S.ThreatsMobileField>
          </S.ThreatsMobileCard>
        ))}
      </S.ThreatsMobileList>
    );
  }

  return (
    <Table.Scroll>
      <Table.Table>
        <thead>
          <tr>
            <Table.Th>{columns.file}</Table.Th>
            <Table.Th>{columns.threat}</Table.Th>
            <Table.Th>{columns.situation}</Table.Th>
            <Table.Th>{columns.detectedAt}</Table.Th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map(row => (
            <tr key={row.path}>
              <Table.Td>{row.path}</Table.Td>
              <Table.Td>{row.name}</Table.Td>
              <Table.Td>
                <ThreatSituationCell row={row} tagsLocale={tagsLocale} />
              </Table.Td>
              <Table.Td>{formatThreatDateTime(row.detectedAt)}</Table.Td>
            </tr>
          ))}
        </tbody>
      </Table.Table>
    </Table.Scroll>
  );
};

const ThreatsTableSection = ({ requestPlanThreatsList, showExportButton } : requestPlanThreatsPorps) => {
  const { gatorProtect: gatorProtectLocale } = useLocale() as GatorProtectThreatsTableLocale;
  const { gatorProtect } = useSelector((state: GatorProtectReduxState) => state);

  const isDesktop = useIsMdUp();
  const sectionTitle = gatorProtectLocale?.summary?.title ?? '';
  const rawColumns = gatorProtectLocale?.threatsTable?.columns;
  const tagsLocale = gatorProtectLocale?.threatsTable?.tags ?? {} as TagsLocaleTypes;

  const columns: ThreatsTableColumns = {
    file: rawColumns?.file ?? '',
    threat: rawColumns?.threat ?? '',
    situation: rawColumns?.situation ?? '',
    detectedAt: rawColumns?.detectedAt ?? '',
  };

  const rawHeader = gatorProtectLocale?.threatsTable?.header;
  const exportButtonAriaLabel = rawHeader?.exportButtonAriaLabel ?? '';
  const infoPlaceholder = rawHeader?.infoPlaceholder ?? '';

  const handleExportClick = useCallback(() => {
    // Reserved for future export wiring (Gator Protect threats table).
  }, []);

  const threatsList = useMemo(
    () => gatorProtect?.planThreats?.data ?? null,
    [gatorProtect?.planThreats?.data],
  );

  const { page: pageMeta, files } = threatsList ?? {
    page: {
      currentPage: 1, perPage: 10, total: 0, lastPage: 1,
    },
    files: [],
  };
  const [page, setPage] = useState(pageMeta?.currentPage || 1);

  const tableRows = useMemo(() => {
    if (page !== pageMeta?.currentPage) {
      return [];
    }
    return files;
  }, [page, pageMeta?.currentPage, files]);


  const handleChangePage = (newPage:number) => {
    requestPlanThreatsList && requestPlanThreatsList(newPage);
    setPage(newPage);
  };

  return (
    <GatorProtectPanel data-testid="threats-table-section">
      <S.ThreatsTableSectionHeader>
        {isDesktop ? (
          <S.ThreatsTableSectionHeaderActions>
            <S.ThreatsTableSectionTitle>{sectionTitle}</S.ThreatsTableSectionTitle>
            <S.ActionsWrapper>
              {showExportButton && (
              <Button
                iconLeft={<IconDownload color="highPure" secondaryColor="transparent" />}
                variant="primary"
                size="medium2"
                label={exportButtonAriaLabel}
                onClick={handleExportClick}
              />
              )}
              <S.ThreatsTableSectionInfoStrip aria-live="polite">
                <IconCalendar color="lowLight" size="sm" />
                {infoPlaceholder}
              </S.ThreatsTableSectionInfoStrip>
            </S.ActionsWrapper>

          </S.ThreatsTableSectionHeaderActions>
        ) : (
          <>
            <S.ThreatsTableSectionMobileTopRow>
              <S.ThreatsTableSectionMobileTitleGroup>
                <S.ThreatsTableSectionMobileTitle>
                  {sectionTitle}
                  <S.ThreatsTableSectionMobileInfoIcon>
                    <IconFilledInfo color="primaryBlue" size="sm" />
                  </S.ThreatsTableSectionMobileInfoIcon>
                </S.ThreatsTableSectionMobileTitle>
              </S.ThreatsTableSectionMobileTitleGroup>

              {showExportButton && (
                <Button
                  iconLeft={<IconDownload color="primaryBlue" />}
                  variant="secondary"
                  notFillSVG
                  size="small"
                  label=""
                  onClick={handleExportClick}
                />
              )}

            </S.ThreatsTableSectionMobileTopRow>
            <S.ThreatsTableSectionInfoStrip aria-live="polite">
              <IconCalendar color="lowLight" size="sm" />
              {infoPlaceholder}
            </S.ThreatsTableSectionInfoStrip>
          </>
        )}

      </S.ThreatsTableSectionHeader>
      <Table.Provider<GatorProtectThreatFile>
        data={tableRows}
        data-testid="gator-protect-threats-table"
      >
        <ThreatsTableLayout
          columns={columns}
          loading={gatorProtect?.planThreats?.loading}
          tagsLocale={tagsLocale}
        />
      </Table.Provider>

      <TablePagination
        mode="local"
        page={page}
        pageSize={pageMeta?.perPage || 10}
        totalItems={pageMeta?.total || 1}
        onPageChange={handleChangePage}
      />
    </GatorProtectPanel>
  );
};

export default ThreatsTableSection;
